import { getCredential } from './credentialStore'
import { isWipeAckUrl, rejectedGrantForWipeAck } from './wipeAckCredential'

/**
 * Native B1 bearer selection (NB1-8): the credential follows the URL family, never the active
 * mode. Grant-family endpoints get the grant (`gs_`) credential when one exists, everything else
 * the organizer (`od_`) one; each falls back to the other. This mirrors web, where both cookies
 * ride every request — and it is why a queued visit posts with the grant credential even while
 * the organizer is in organizer mode (A1's queue must never be credited to the staff identity).
 *
 * Return credentials are never sent as a bearer. On web `getCredential` is null, so this is too.
 *
 * [LEGAL] One exception: `/grant/wipe-ack` with no live grant credential carries the `gs_` a 401
 * just rejected (wipeAckCredential.ts), so a server-driven revoke can still stamp WipeConfirmedAt.
 * No other URL ever sees that value.
 */
export const GRANT_URL_PREFIXES = ['/grant', '/canvassing', '/lobby/attendee'] as const

export function credentialKindFor(url: string | undefined): 'grant' | 'organizer' {
  const u = url ?? ''
  const isGrant = GRANT_URL_PREFIXES.some(
    (p) => u === p || u.startsWith(p + '/') || u.startsWith(p + '?'),
  )
  return isGrant ? 'grant' : 'organizer'
}

export function bearerFor(url: string | undefined): string | null {
  const preferred = credentialKindFor(url)
  const other = preferred === 'grant' ? 'organizer' : 'grant'
  return (
    getCredential(preferred) ??
    (isWipeAckUrl(url) ? rejectedGrantForWipeAck() : null) ??
    getCredential(other)
  )
}
